import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Chip, CircularProgress, LinearProgress, MenuItem, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import {
  tpms, HEALTH_LABEL, HEALTH_RANK, healthColour,
} from './api';
import TyreStrip from './TyreStrip';
import { formatDateTime } from '../common/util/datetime';
import DownloadTable from '../shell/DownloadTable';

// The tyre user's home page.
//
// Not the tracking map with the tyres bolted on: this user has no business seeing a map and cannot
// obtain one. What they need on arriving is which lorry needs attention first, so the table leads
// with the single worst tyre on each vehicle rather than making somebody open eighteen of them.
const ago = (value) => {
  const at = Date.parse(value);
  if (!Number.isFinite(at)) return 'never';
  const minutes = Math.round((Date.now() - at) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  return hours < 24 ? `${hours} h ago` : `${Math.round(hours / 24)} d ago`;
};

const Tile = ({ label, value, colour }) => (
  <Paper variant="outlined" sx={{ p: 2, minWidth: 132, flex: '1 1 132px' }}>
    <Typography variant="h4" fontWeight={700} color={colour}>{value}</Typography>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
  </Paper>
);

const TpmsDashboard = () => {
  const navigate = useNavigate();
  const [fleet, setFleet] = useState(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [health, setHealth] = useState('ALL');

  useEffect(() => {
    const controller = new AbortController();
    const load = () => tpms('/api/tpms/fleet', 'GET', null, controller.signal)
      .then((data) => { setFleet(data); setError(''); })
      .catch((e) => { if (e.name !== 'AbortError') setError(e.message); });
    load();
    const timer = setInterval(load, 20000);
    return () => { controller.abort(); clearInterval(timer); };
  }, []);

  const rows = useMemo(() => {
    const all = fleet?.rows || [];
    return all
      .filter((r) => (health === 'ALL' || r.health === health)
        && (r.vehicle || '').toLowerCase().includes(search.trim().toLowerCase()))
      .sort((a, b) => (HEALTH_RANK[a.health] ?? 9) - (HEALTH_RANK[b.health] ?? 9)
        || (a.vehicle || '').localeCompare(b.vehicle || ''));
  }, [fleet, health, search]);

  // Counted across every tyre on every vehicle, so the headline is about rubber rather than lorries.
  const tyreTotals = useMemo(() => {
    const totals = {
      total: 0, NORMAL: 0, WARNING: 0, CRITICAL: 0, unknown: 0,
    };
    (fleet?.rows || []).forEach((row) => (row.tyres || []).forEach((tyre) => {
      totals.total += 1;
      if (tyre.health === 'NORMAL' || tyre.health === 'WARNING' || tyre.health === 'CRITICAL') totals[tyre.health] += 1;
      else totals.unknown += 1;
    }));
    return totals;
  }, [fleet]);

  if (!fleet && !error) {
    return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700}>Tyre health</Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {`Pressure in ${fleet?.rows?.[0]?.pressureUnit || 'PSI'} for every tyre, worst vehicles first.`}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Counted in TYRES, not vehicles, which is how a tyre shop counts.
          A lorry with one soft tyre is a critical vehicle but one critical tyre, and a headline
          that says "3 critical" meaning three lorries tells a fitter nothing about how much work
          is in front of them. Vehicles are still counted, as their own figure. */}
      <Stack direction="row" gap={2} flexWrap="wrap" sx={{ mb: 2 }}>
        <Tile label="Vehicles" value={fleet?.vehicles ?? 0} />
        <Tile label="Tyres" value={tyreTotals.total} />
        <Tile label={HEALTH_LABEL.CRITICAL} value={tyreTotals.CRITICAL} colour={tyreTotals.CRITICAL ? 'error.main' : 'text.primary'} />
        <Tile label={HEALTH_LABEL.WARNING} value={tyreTotals.WARNING} colour={tyreTotals.WARNING ? 'warning.main' : 'text.primary'} />
        <Tile label={HEALTH_LABEL.NO_DATA} value={tyreTotals.unknown} colour={tyreTotals.unknown ? 'text.secondary' : 'text.primary'} />
        <Tile label="Open alerts" value={fleet?.activeAlerts ?? 0} colour={fleet?.activeAlerts ? 'error.main' : 'text.primary'} />
      </Stack>

      {!!tyreTotals.total && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{
            display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', bgcolor: 'action.disabledBackground',
          }}
          >
            {[['NORMAL', 'success.main'], ['WARNING', 'warning.main'], ['CRITICAL', 'error.main'], ['unknown', 'grey.500']].map(([key, colour]) => (
              tyreTotals[key] ? (
                <Box key={key} sx={{ width: `${(tyreTotals[key] / tyreTotals.total) * 100}%`, bgcolor: colour }} />
              ) : null
            ))}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {`${tyreTotals.NORMAL} of ${tyreTotals.total} tyres within tolerance`}
          </Typography>
        </Box>
      )}

      <Stack direction="row" gap={1.5} flexWrap="wrap" sx={{ mb: 2 }}>
        <TextField size="small" label="Find a vehicle" value={search} onChange={(e) => setSearch(e.target.value)} inputProps={{ maxLength: 60 }} />
        <TextField size="small" select label="Condition" value={health} onChange={(e) => setHealth(e.target.value)} sx={{ minWidth: 170 }}>
          <MenuItem value="ALL">All conditions</MenuItem>
          {Object.keys(HEALTH_LABEL).map((key) => <MenuItem key={key} value={key}>{HEALTH_LABEL[key]}</MenuItem>)}
        </TextField>
      </Stack>

      {!fleet?.vehicles && !error && (
        <Alert severity="info">No vehicles are set up for tyre monitoring yet.</Alert>
      )}

      {!!rows.length && (
        <>
          {/* The strip of tyres is a drawing. What a spreadsheet can hold is the count behind each
              colour, which is what the strip is saying anyway. */}
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
            <DownloadTable
              name="tyre-health"
              rows={rows}
              columns={[
                ['Vehicle', (row) => row.vehicle],
                ['Condition', (row) => HEALTH_LABEL[row.health] || row.health],
                ['Tyres', (row) => (row.tyres || []).length],
                ['Critical', (row) => (row.tyres || []).filter((t) => t.health === 'CRITICAL').length],
                ['Warning', (row) => (row.tyres || []).filter((t) => t.health === 'WARNING').length],
                ['No data', (row) => (row.tyres || []).filter((t) => t.health === 'NO_DATA').length],
                ['Open alerts', (row) => row.openAlerts || 0],
                ['Last reading', (row) => (row.lastUpdateAt ? formatDateTime(row.lastUpdateAt) : '')],
              ]}
            />
          </Stack>
          <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Vehicle', 'Tyres', 'Condition', 'Alerts', 'Last reading'].map((c) => (
                    <TableCell key={c}>{c}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.vehicleId}
                    hover
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/tpms/vehicles/${row.vehicleId}`)}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/tpms/vehicles/${row.vehicleId}`); }}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{row.vehicle}</Typography>
                    </TableCell>
                    <TableCell>
                      <TyreStrip
                        tyres={row.tyres}
                        temperatureUnit={row.temperatureUnit}
                        onSelect={(position) => navigate(`/tpms/vehicles/${row.vehicleId}?tyre=${encodeURIComponent(position)}`)}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={HEALTH_LABEL[row.health] || row.health} color={healthColour(row.health)} variant={healthColour(row.health) === 'default' ? 'outlined' : 'filled'} />
                    </TableCell>
                    <TableCell>{row.openAlerts || ''}</TableCell>
                    <TableCell title={row.lastUpdateAt ? formatDateTime(row.lastUpdateAt) : undefined}>{ago(row.lastUpdateAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      {!!fleet && !rows.length && !!fleet.vehicles && (
        <Alert severity="info">No vehicle matches that filter.</Alert>
      )}
      {!fleet && !error && <LinearProgress />}
    </Box>
  );
};

export default TpmsDashboard;
